import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { Exam, type ExamState } from '../../exam.js';
import { buildMsGsPDFBytes, walkExamTasks } from '../generate-ms-gs-file.js';

export const downloadMsGsToolDef = [
  {
    type: 'function',
    function: {
      name: 'download_ms_gs',
      description:
        'Löst den Download der Musterlösung und/oder des Bewertungsschemas aus. Darf NUR aufgerufen werden, wenn der Nutzer explizit danach fragt UND wenn die entsprechenden Inhalte bereits generiert oder hochgeladen wurden.',
      parameters: {
        type: 'object',
        properties: {
          examId: {
            type: 'string',
            description: 'Die ID der aktuellen Klausur',
          },
          type: {
            type: 'string',
            enum: ['masterSolution', 'gradingScheme', 'both'],
            description:
              '"masterSolution" für nur die Musterlösung, "gradingScheme" für nur das Bewertungsschema, "both" für beides.',
          },
        },
        required: ['examId', 'type'],
      },
    },
  },
];

function hasMsContent(exam: any): boolean {
  return walkExamTasks(Array.isArray(exam?.tasks) ? exam.tasks : []).some(row => row.masterSolution.trim().length > 0);
}

function hasGsContent(exam: any): boolean {
  return walkExamTasks(Array.isArray(exam?.tasks) ? exam.tasks : []).some(row => row.gradingCriteria.trim().length > 0);
}

export async function handleDownloadMsGs(exam: Exam, toolCallArgs: any): Promise<object> {
  const examIdToUse = toolCallArgs.examId || exam?._id?.toString();
  const downloadType: 'masterSolution' | 'gradingScheme' | 'both' = toolCallArgs.type ?? 'both';

  if (!examIdToUse) {
    return { content: 'Fehler: Keine Klausur-ID.' };
  }

  const examCollection = await getCollection(Exam);
  const currentExam = await examCollection.findOne({ _id: examIdToUse });
  if (!currentExam) {
    return { content: 'Fehler: Klausur nicht gefunden.' };
  }

  const needsMs = (downloadType as string) === 'masterSolution' || (downloadType as string) === 'both';
  const needsGs = (downloadType as string) === 'gradingScheme' || (downloadType as string) === 'both';
  const hasMs = hasMsContent(currentExam);
  const hasGs = hasGsContent(currentExam);

  if ((needsMs && !hasMs) && (needsGs && !hasGs)) {
    return {
      content: 'SYSTEM INFO: Es wurde noch keine Musterlösung oder Bewertungsschema generiert oder hochgeladen. Bitte den Nutzer darauf hinweisen.',
    };
  }
  if (needsMs && !needsGs && !hasMs) {
    return { content: 'SYSTEM INFO: Es wurde noch keine Musterlösung generiert oder hochgeladen.' };
  }
  if (needsGs && !needsMs && !hasGs) {
    return { content: 'SYSTEM INFO: Es wurde noch kein Bewertungsschema generiert oder hochgeladen.' };
  }

  try {
    const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
    const currentUserId = currentUser?._id;
    if (!currentUserId) {
      return { content: 'Fehler: Nutzer nicht eingeloggt.' };
    }

    const pdfBytes = await buildMsGsPDFBytes(currentExam, downloadType);
    const dateStr = new Date().toISOString().slice(0, 10);
    const suffix =
      downloadType === 'masterSolution'
        ? 'Musterloesung'
        : downloadType === 'gradingScheme'
          ? 'Bewertungsschema'
          : 'Musterloesung-Bewertungsschema';
    const filename = `${suffix}_${dateStr}.pdf`;

    const file = await PDFFile.createFromBuffer(Buffer.from(pdfBytes), filename, {
      userID: String(currentUserId),
      examId: String(examIdToUse),
      fileType: 'ms-gs-export',
    });

    if (!file._id) {
      return { content: 'Fehler: Datei konnte nicht erstellt werden.' };
    }

    const label =
      downloadType === 'masterSolution'
        ? 'Musterlösung'
        : downloadType === 'gradingScheme'
          ? 'Bewertungsschema'
          : 'Musterlösung und Bewertungsschema';
    return {
      content: `SYSTEM INFO: Der Download der ${label} wird jetzt ausgelöst.`,
      pendingDownload: file._id,
    };
  } catch (err) {
    logger.error({ err, examIdToUse }, 'download_ms_gs: Fehler beim Erstellen der Datei');
    return { content: 'Fehler beim Erstellen der Download-Datei.' };
  }
}

export function downloadMsGsAvailability(_examState: ExamState): boolean {
  return true;
}
